import { useState, type ReactNode } from 'react';
import {
  terminalParamSecretViolation,
  type TerminalParamDescriptor,
  type TerminalParamSecretViolation,
} from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractApiError } from '@/lib/api-error';
import {
  useSetTerminalParamSecret,
  type TerminalParamAssignment,
  type TerminalParamScope,
} from '@/hooks/use-terminal-params';

type Translate = ReturnType<typeof useT>;

/** The operator's words for each policy refusal; literal keys for the parity guard. */
export function secretViolationMessage(
  t: Translate,
  violation: TerminalParamSecretViolation,
  /** MICRO-46: the descriptor's `secretMinLength`, when it asks for more than 8. */
  minLength?: number,
): string {
  switch (violation) {
    case 'tooShort':
      return minLength === undefined
        ? t('parameters.secret.tooShort', 'Use at least 8 characters.')
        : t('parameters.secret.tooShortMin', 'Use at least {n} characters.').replace(
            '{n}',
            String(minLength),
          );
    case 'tooLong':
      return t('parameters.secret.tooLong', 'Use at most 64 characters.');
    case 'charset':
      return t(
        'parameters.secret.charset',
        'Use only Latin letters, digits and keyboard symbols, with no space at the start or end.',
      );
    case 'needsLetterAndDigit':
      return t('parameters.secret.needsLetterAndDigit', 'Use at least one letter and one digit.');
    case 'repeated':
      return t('parameters.secret.repeated', 'Use at least three different characters.');
  }
}

/**
 * The editor for a `secret: 'passwordHash'` parameter (MICRO-10).
 *
 * Driven by the descriptor flag, not by a key. There is no value to show:
 * Back Office keeps only a hash and the API sends a placeholder, so the
 * control says whether a password is set at this scope and when it last
 * changed, and offers to set or change it. The new password is typed twice,
 * checked against the same policy the API applies, posted to the secret
 * endpoint and dropped from state as soon as the request settles. Clearing is
 * the row's ordinary "Clear…".
 */
export function SecretParamControl({
  descriptor,
  scope,
  scopeId,
  setHere,
  disabled,
  impact,
  id,
}: {
  descriptor: TerminalParamDescriptor;
  scope: TerminalParamScope;
  scopeId: string;
  setHere: TerminalParamAssignment | undefined;
  disabled: boolean;
  /** The blast-radius note, shown while the form is open. */
  impact: ReactNode;
  id: string;
}) {
  const t = useT();
  const setSecret = useSetTerminalParamSecret();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const minLength = descriptor.secretMinLength;
  const violation = password === '' ? null : terminalParamSecretViolation(password, minLength);
  const mismatch = repeat !== '' && repeat !== password;
  const canSave =
    password !== '' && violation === null && repeat === password && !setSecret.isPending;

  const close = () => {
    setPassword('');
    setRepeat('');
    setOpen(false);
  };

  const save = async () => {
    if (!canSave) return;
    setError(null);
    setSaved(false);
    try {
      await setSecret.mutateAsync({ key: descriptor.key, scope, scopeId, password });
      close();
      setSaved(true);
    } catch (cause) {
      setError(extractApiError(cause));
    } finally {
      // Never keep a typed password around after the request, success or not:
      // not in the inputs, and not in the mutation's variables either (P10-2).
      setPassword('');
      setRepeat('');
      setSecret.reset();
    }
  };

  return (
    <div className="space-y-2" data-secret-param={descriptor.key}>
      <p className="text-sm" aria-label={descriptor.label}>
        {setHere === undefined ? (
          t('parameters.secret.notSetHere', 'No password is set at this scope.')
        ) : (
          <>
            {t('parameters.secret.setHere', 'A password is set at this scope.')}{' '}
            <span className="text-xs text-muted-foreground">
              {t('parameters.secret.lastChanged', 'Last changed')}{' '}
              {new Date(setHere.updatedAt).toLocaleString()}
            </span>
          </>
        )}
      </p>
      <p className="text-xs text-muted-foreground">
        {t(
          'parameters.secret.notShown',
          'Back Office keeps only a hash of the password and cannot show it.',
        )}
      </p>
      {saved && (
        <p className="text-xs text-emerald-700">
          {t('parameters.secret.saved', 'Password saved.')}
        </p>
      )}
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
      {!open && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          aria-label={`${
            setHere === undefined
              ? t('parameters.secret.set', 'Set password…')
              : t('parameters.secret.change', 'Change password…')
          } ${descriptor.label}`}
          onClick={() => {
            setSaved(false);
            setError(null);
            setOpen(true);
          }}
        >
          {setHere === undefined
            ? t('parameters.secret.set', 'Set password…')
            : t('parameters.secret.change', 'Change password…')}
        </Button>
      )}
      {open && (
        <div className="space-y-2 rounded-md border bg-background p-3">
          <label className="block space-y-1 text-xs" htmlFor={id}>
            <span>{t('parameters.secret.newPassword', 'New password')}</span>
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              className="max-w-sm"
              value={password}
              disabled={setSecret.isPending}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <label className="block space-y-1 text-xs" htmlFor={`${id}-repeat`}>
            <span>{t('parameters.secret.repeatPassword', 'Repeat the password')}</span>
            <Input
              id={`${id}-repeat`}
              type="password"
              autoComplete="new-password"
              className="max-w-sm"
              value={repeat}
              disabled={setSecret.isPending}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            {minLength === undefined
              ? t(
                  'parameters.secret.policy',
                  'At least 8 characters, with at least one letter and one digit. Latin letters, digits and keyboard symbols only.',
                )
              : t(
                  'parameters.secret.policyMin',
                  'At least {n} characters, with at least one letter and one digit. Latin letters, digits and keyboard symbols only.',
                ).replace('{n}', String(minLength))}
            {descriptor.secretDistinctFrom !== undefined &&
              ` ${t('parameters.secret.distinct', 'It must not be the same as the lane’s other service passwords.')}`}
          </p>
          {violation !== null && (
            <p className="text-sm text-destructive">
              {secretViolationMessage(t, violation, minLength)}
            </p>
          )}
          {mismatch && (
            <p className="text-sm text-destructive">
              {t('parameters.secret.mismatch', 'The two passwords differ.')}
            </p>
          )}
          {impact}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!canSave}
              aria-label={`${t('parameters.secret.save', 'Save password')} ${descriptor.label}`}
              onClick={() => void save()}
            >
              {setSecret.isPending
                ? t('common.saving', 'Saving…')
                : t('parameters.secret.save', 'Save password')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={setSecret.isPending}
              aria-label={`${t('common.cancel', 'Cancel')} ${descriptor.label}`}
              onClick={close}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
